import React, {
  useEffect,
  useState,
  lazy,
  Suspense,
  memo,
} from "react";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import { useInView } from "react-intersection-observer";
import { Helmet } from "react-helmet-async";

import HeroAnimation from "../../components/Hero/HeroAnimation";
import FullScreenLoader from "../../components/FullScreenLoader";

/* ======================================================
   Lazy Loaded Sections
====================================================== */
// const Industries = lazy(() => import("../Industries/Industries"));
const StatsSection = lazy(() => import("./StatsSection"));
const IndustryReach = lazy(() => import("../../components/IndustryReach/IndustryReach"));
const ClientelePage = lazy(() => import("../Clientpage/ClientelePage.jsx"));
const MapContainer = lazy(() => import("../../components/Map/MapContainer"));
const WhyMinivel = lazy(() => import("../About/MinivelWhy"));
const DecadeOfExcellence = lazy(() => import("../../components/DecadeOfExcellence/DecadeOfExcellence"));

/* ======================================================
   Skeleton Loader
====================================================== */
const Skeleton = ({ height = "400px" }) => (
  <div
    className="w-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse"
    style={{ height }}
  />
);

/* ======================================================
   Image Preload Utility (SAFE)
====================================================== */
const preloadImages = (urls = []) => {
  if (!Array.isArray(urls)) return;
  urls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
};

/* ======================================================
   Lazy Section Wrapper (OPTIMIZED)
====================================================== */
const LazySection = memo(function LazySection({
  Component,
  height,
  preloadNext,
  preloadImagesList = [],
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px",
  });

  useEffect(() => {
    if (!inView) return;

    if (typeof preloadNext === "function") {
      preloadNext().catch(() => { });
    }

    if (preloadImagesList.length) {
      preloadImages(preloadImagesList);
    }
  }, [inView, preloadNext, preloadImagesList]);

  return (
    <div ref={ref} style={{ minHeight: height }} className="w-full">
      {inView ? (
        <Suspense fallback={<Skeleton height={height} />}>
          <Component />
        </Suspense>
      ) : (
        <Skeleton height={height} />
      )}
    </div>
  );
});




/* ======================================================
   Landing Page
====================================================== */
function LandingPage() {
  const [pageLoading, setPageLoading] = useState(true);
  const [consentGiven, setConsentGiven] = useState(false);

  /* Initial Loader */
  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  /* Cookie Consent Check + HERO preload ONLY */
  useEffect(() => {
    const consent = getCookieConsentValue("CookieConsent") === "true";
    setConsentGiven(consent);

    // 🔥 ONLY hero image preload
    // preloadImages(["/assets/images/hero.webp"]);
  }, []);

  /* Preload JS chunks */
  const preloadStatsSection = () => import("./StatsSection");
  const preloadIndustryReach = () => import("../../components/IndustryReach/IndustryReach");
  const preloadClientelePage = () => import("../Clientpage/ClientelePage.jsx");
  const preloadMapContainer = () =>
    import("../../components/Map/MapContainer");
  const preloadWhyMinivel = () => import("../About/MinivelWhy");

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Minivel Services Pvt. Ltd.",
    "url": "https://www.minivel.com",
    "logo": "https://www.minivel.com/assets/logo.png",
    "sameAs": [
      "https://www.linkedin.com/company/minivel-services",
      "https://clutch.co/profile/minivel-services",
      "https://www.goodfirms.co/company/minivel-services",
      "https://www.justdial.com/Mumbai/Minivel-Services",
      "https://www.indiamart.com/minivel-services",
      "https://www.startupindia.gov.in/content/sih/en/profile.Startup.html?id=minivel"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-8956781401",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": "en"
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden relative bg-[#020617]">
      <Helmet>
        <title>Minivel Services | Best HR Consultancy & Recruitment Agency in India</title>
        <meta
          name="description"
          content="Minivel Services is a top-rated HR consultancy in India offering Permanent Recruitment, RPO, Staffing, and Payroll solutions. ISO 9001:2015 Certified."
        />
        <meta
          name="keywords"
          content="Best HR Consultancy India, Recruitment Agency Mumbai, RPO Services, Payroll Outsourcing, Staffing Solutions"
        />
      </Helmet>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      {/* Full Screen Loader */}
      {pageLoading && <FullScreenLoader />}

      {/* HERO */}
      <HeroAnimation />

      {/* DECADE OF EXCELLENCE */}
      <LazySection
        Component={DecadeOfExcellence}
        height="600px"
        preloadNext={preloadWhyMinivel}
      />

      {/* WHY MINIVEL */}
      <LazySection
        Component={WhyMinivel}
        height="600px"
        preloadNext={preloadIndustryReach}
      />

      {/* INDUSTRY REACH */}
      <LazySection
        Component={IndustryReach}
        height="450px"
        preloadNext={preloadStatsSection}
      />
      
      {/* STATS */}
      <LazySection
        Component={StatsSection}
        height="400px"
        preloadNext={preloadClientelePage}
      />

      {/* CLIENTELE (logos lazy inside component) */}
      <LazySection
        Component={ClientelePage}
        height="360px"
        preloadNext={preloadMapContainer}
      />

      <LazySection
        Component={MapContainer}
        height="500px"
      />

      {/* COOKIE CONSENT */}
      {!consentGiven && (
        <CookieConsent
          location="bottom"
          buttonText="Accept"
          cookieName="CookieConsent"
          expires={365}
          onAccept={() => setConsentGiven(true)}
          style={{
            background: "#212124",
            color: "#fff",
            fontSize: "14px",
            zIndex: 9999,
          }}
          buttonStyle={{
            background: "#70be44",
            color: "#fff",
            fontWeight: "bold",
            borderRadius: "6px",
            padding: "10px 20px",
          }}
        >
          We use cookies to enhance your experience.
        </CookieConsent>
      )}
    </div>
  );
}

export default LandingPage;
